import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { Crown, Skull, Leaf, Calendar, Mail, ShieldCheck, Trophy, ExternalLink, Pencil, Check, X, Lock, Loader2, Sparkles, AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { TIER_LABELS, api } from "@/lib/api";
import { RoleBadge } from "@/components/RoleBadge";
import { RankBadge } from "@/components/RankBadge";
import { StreakBadge } from "@/components/StreakBadge";
import { toast } from "sonner";

export default function Account() {
    const { user, logout, loading, refresh } = useAuth();
    const nav = useNavigate();
    const [search] = useSearchParams();
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [pendingPayments, setPendingPayments] = useState([]);
    const [recovering, setRecovering] = useState(null);
    const [dismissing, setDismissing] = useState(null);

    // Load any pending (unrecovered) payments for this user
    useEffect(() => {
        if (!user) return;
        let alive = true;
        (async () => {
            try {
                const res = await api.get("/billing/my-pending");
                if (alive) setPendingPayments(res.data?.items || []);
            } catch (e) { /* noop */ }
        })();
        return () => { alive = false; };
    }, [user]);

    const recoverPayment = async (sessionId) => {
        setRecovering(sessionId);
        try {
            const res = await api.post(`/billing/recover/${sessionId}`);
            if (res.data?.applied) {
                toast.success("Purchase recovered \u2014 your prize is now in your account!");
                await refresh();
                setPendingPayments((prev) => prev.filter((p) => p.session_id !== sessionId));
            } else if (res.data?.payment_status === "paid") {
                toast.success("Payment confirmed \u2014 prize granted!");
                await refresh();
                setPendingPayments((prev) => prev.filter((p) => p.session_id !== sessionId));
            } else {
                toast("Payment not yet confirmed by Stripe \u2014 try again in a moment, or contact support.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Recovery failed");
        } finally {
            setRecovering(null);
        }
    };

    const dismissPayment = async (sessionId) => {
        if (!window.confirm("Confirm you've already received this purchase? This will hide the alert.")) return;
        setDismissing(sessionId);
        try {
            await api.post(`/billing/dismiss/${sessionId}`);
            toast.success("Got it \u2014 alert cleared!");
            setPendingPayments((prev) => prev.filter((p) => p.session_id !== sessionId));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to dismiss");
        } finally {
            setDismissing(null);
        }
    };

    const describePending = (p) => {
        if (p.kind === "banner") return "Banner purchase";
        if (p.kind === "giveaway_entry") return `Giveaway entries (${p.entries || 1})`;
        if (p.kind === "subscription") return `Subscription (${p.tier || "?"})`;
        return p.kind || "Payment";
    };

    useEffect(() => {
        if (!loading && !user) nav("/login?next=/account");
    }, [loading, user, nav]);

    // Celebrate a successful payment: show banner, refresh user (webhook may take a few sec)
    useEffect(() => {
        if (search.get("success") !== "true") return;
        setShowSuccess(true);
        // Poll for tier upgrade for up to 15s, then give up and rely on periodic refresh
        let tries = 0;
        const maxTries = 5;
        const interval = setInterval(async () => {
            tries += 1;
            await refresh();
            if (tries >= maxTries) clearInterval(interval);
        }, 3000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    if (loading) {
        return (
            <div className="min-h-screen pp-bg-0 text-white">
                <Nav />
                <section className="py-12 sm:py-16">
                    <div className="max-w-3xl mx-auto px-4">
                        <div className="h-8 w-40 mb-4 rounded pp-bg-1 animate-pulse" />
                        <div className="h-64 rounded-xl pp-bg-1 animate-pulse" />
                    </div>
                </section>
                <Footer />
            </div>
        );
    }
    if (!user) return null;

    const tier = user.effective_tier || user.tier || "free";
    const tierColor = tier === "sports_premium" ? "#ef4444" : tier === "pro" ? "#a855f7" : "#22c55e";
    const TierIcon = tier === "sports_premium" ? Skull : tier === "pro" ? Crown : Leaf;
    const expires = user.tier_expires_at ? new Date(user.tier_expires_at).toLocaleString() : "—";

    // Eligibility for changing display name: admin/support OR pro/sports_premium tier
    const canChangeName = user.role === "admin" || user.role === "support" || tier === "pro" || tier === "sports_premium";

    const startEdit = () => {
        setNameDraft(user.display_name || "");
        setEditingName(true);
    };
    const cancelEdit = () => {
        setEditingName(false);
        setNameDraft("");
    };
    const saveName = async () => {
        const trimmed = nameDraft.trim();
        if (trimmed.length < 2) { toast.error("Display name must be at least 2 characters"); return; }
        if (trimmed.length > 30) { toast.error("Display name must be 30 characters or fewer"); return; }
        if (trimmed === (user.display_name || "")) { cancelEdit(); return; }
        setSaving(true);
        try {
            await api.patch("/users/me/display-name", { display_name: trimmed });
            toast.success("Display name updated");
            await refresh();
            setEditingName(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update name");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-12 sm:py-16">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="mb-6">
                        <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">Your account</p>
                        <h1 className="font-display text-3xl sm:text-4xl tracking-widest">ACCOUNT</h1>
                    </div>

                    {/* Payment success banner */}
                    {showSuccess && (
                        <Card className="mb-6 border-[#22c55e]/60" style={{ background: "linear-gradient(135deg, #07130b, #0a1f0e)", boxShadow: "0 0 32px rgba(34,197,94,0.22)" }} data-testid="account-success-banner">
                            <CardContent className="p-5 flex items-start gap-3">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#22c55e]22 border border-[#22c55e]66 flex-shrink-0">
                                    <Sparkles className="w-5 h-5 text-[#22c55e]" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-display text-xl tracking-widest text-white">PAYMENT COMPLETE — WELCOME IN 🎉</p>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">
                                        Your subscription is being activated. This can take 5-10 seconds. If your tier doesn't update shortly, refresh the page — if it's still stuck, {" "}
                                        <Link to="/messages" className="text-[#22d3ee] hover:underline">DM support</Link> and we'll fix it fast.
                                    </p>
                                </div>
                                <button onClick={() => setShowSuccess(false)} className="text-[#b7b7c6] hover:text-white" data-testid="account-success-banner-close">
                                    <X className="w-4 h-4" />
                                </button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stuck payments — self-recovery */}
                    {pendingPayments.length > 0 && (
                        <Card className="mb-6 border-[#facc15]/60" style={{ background: "linear-gradient(135deg, #1a1503, #1f1a05)", boxShadow: "0 0 24px rgba(250,204,21,0.18)" }} data-testid="account-pending-payments-card">
                            <CardContent className="p-5">
                                <div className="flex items-start gap-3 mb-3">
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#facc15]22 border border-[#facc15]66 flex-shrink-0">
                                        <AlertTriangle className="w-5 h-5 text-[#facc15]" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-display text-xl tracking-widest text-white">PAYMENT NEEDS A NUDGE</p>
                                        <p className="mt-1 text-sm text-[#b7b7c6]">
                                            We see {pendingPayments.length} recent purchase{pendingPayments.length === 1 ? "" : "s"} of yours that hasn't been delivered yet. Click "Recover" to confirm with Stripe and unlock it instantly. We also auto-check every minute.
                                        </p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {pendingPayments.map((p) => (
                                        <div key={p.session_id} className="flex items-center justify-between gap-3 pp-bg-0 border border-[#242436] rounded-md px-3 py-2.5" data-testid={`pending-payment-${p.session_id}`}>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm text-white truncate">{describePending(p)} <span className="text-[#facc15] font-mono">${(p.amount || 0).toFixed(2)}</span></p>
                                                <p className="text-[10px] font-mono text-[#8b8ba0] truncate">
                                                    {new Date(p.created_at).toLocaleString()} • {p.session_id.slice(0, 16)}…
                                                </p>
                                            </div>
                                            <div className="flex gap-2 flex-shrink-0">
                                                <Button
                                                    onClick={() => recoverPayment(p.session_id)}
                                                    disabled={recovering === p.session_id || dismissing === p.session_id}
                                                    size="sm"
                                                    className="bg-[#facc15] hover:brightness-110 text-[#1a1503] font-bold"
                                                    data-testid={`recover-payment-${p.session_id}`}
                                                    title="Re-check Stripe and unlock your purchase"
                                                >
                                                    {recovering === p.session_id
                                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        : <span className="inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Recover</span>}
                                                </Button>
                                                <Button
                                                    onClick={() => dismissPayment(p.session_id)}
                                                    disabled={recovering === p.session_id || dismissing === p.session_id}
                                                    size="sm"
                                                    variant="secondary"
                                                    className="bg-[#07130b] border border-[#22c55e]55 text-[#22c55e] hover:bg-[#0a1f0e]"
                                                    data-testid={`dismiss-payment-${p.session_id}`}
                                                    title="I already got this — clear the alert"
                                                >
                                                    {dismissing === p.session_id
                                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        : <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Got It</span>}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="pp-bg-1 border-[#242436] text-white" data-testid="account-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full font-bold" style={{ background: tierColor, color: tier === "free" ? "#07130b" : "#fff" }}>
                                        {(user.display_name || user.email)[0]?.toUpperCase()}
                                    </span>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-display text-xl tracking-widest" data-testid="account-display-name">{user.display_name || user.email}</p>
                                            <RoleBadge role={user.role} size="lg" />
                                        </div>
                                        <p className="text-sm text-[#b7b7c6] inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {user.email}</p>
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                            <RankBadge rank={user.rank} animate />
                                            {user.role === "admin" || user.role === "support"
                                                ? null
                                                : <StreakBadge streak={user.current_streak} best={user.best_streak} showBest />}
                                        </div>
                                    </div>
                                </div>
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border" style={{ color: tierColor, borderColor: `${tierColor}66`, background: `${tierColor}1a` }} data-testid="account-subscription-tier">
                                    <TierIcon className="w-4 h-4" /> {TIER_LABELS[tier]}
                                </span>
                            </div>
                            {user.rank?.next_label && user.rank.to_next > 0 && (
                                <div className="mt-4 rounded-lg px-4 py-3 border border-[#242436] pp-bg-0">
                                    <p className="text-xs font-mono uppercase text-[#8b8ba0] inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Next rank</p>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">
                                        <span className="text-white font-display tracking-widest">{user.rank.to_next}</span>{" "}
                                        more {user.rank.to_next === 1 ? "day" : "days"} to <span style={{ color: user.rank.color }}>{user.rank.next_label}</span>
                                    </p>
                                </div>
                            )}
                            {user.role === "support" && (
                                <div className="mt-4 rounded-lg px-4 py-3 border border-[#22d3ee]/40 bg-[#06181f]">
                                    <p className="text-sm text-[#22d3ee] inline-flex items-center gap-2 font-mono uppercase"><ShieldCheck className="w-4 h-4" /> You're on the Support team</p>
                                    <p className="mt-1 text-xs text-[#b7b7c6]">Owner granted you the SUPPORT badge. You can post in chat, claim help requests, and DM users.</p>
                                </div>
                            )}

                            {/* Display Name editor */}
                            <div className="mt-6 rounded-lg p-4 border border-[#242436] pp-bg-0" data-testid="account-display-name-section">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs uppercase font-mono text-[#8b8ba0] inline-flex items-center gap-1">
                                            <Pencil className="w-3.5 h-3.5" /> Display name
                                            {!canChangeName && <Lock className="w-3 h-3 text-[#facc15] ml-1" />}
                                        </p>
                                        {!editingName && (
                                            <p className="mt-1 font-display text-lg tracking-widest text-white truncate" data-testid="account-display-name-value">
                                                {user.display_name || "—"}
                                            </p>
                                        )}
                                        {editingName && (
                                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                <Input
                                                    autoFocus
                                                    value={nameDraft}
                                                    onChange={(e) => setNameDraft(e.target.value.slice(0, 30))}
                                                    placeholder="Your new display name"
                                                    className="pp-bg-1 border-[#242436] text-white max-w-xs"
                                                    data-testid="account-display-name-input"
                                                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") cancelEdit(); }}
                                                />
                                                <span className="text-[10px] font-mono text-[#8b8ba0]">{nameDraft.length}/30</span>
                                            </div>
                                        )}
                                    </div>
                                    {!editingName && canChangeName && (
                                        <Button onClick={startEdit} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white" data-testid="account-display-name-edit-button">
                                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                        </Button>
                                    )}
                                    {!editingName && !canChangeName && (
                                        <Button asChild className="bg-[#a855f7] hover:brightness-110 text-white" data-testid="account-display-name-upgrade-cta">
                                            <Link to="/pricing" className="inline-flex items-center gap-1"><Crown className="w-4 h-4" /> Unlock</Link>
                                        </Button>
                                    )}
                                    {editingName && (
                                        <div className="flex gap-2">
                                            <Button onClick={saveName} disabled={saving} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="account-display-name-save-button">
                                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Save</span>}
                                            </Button>
                                            <Button onClick={cancelEdit} disabled={saving} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white" data-testid="account-display-name-cancel-button">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {!canChangeName && (
                                    <p className="mt-2 text-xs text-[#8b8ba0]" data-testid="account-display-name-locked-hint">
                                        Display name changes are unlocked for <span className="text-[#a855f7]">Pro</span> and <span className="text-[#ef4444]">Sports Premium</span> subscribers, plus staff. Subscribe to unlock.
                                    </p>
                                )}
                                {canChangeName && !editingName && (
                                    <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#22c55e]">
                                        {user.role === "admin" || user.role === "support" ? "STAFF PERK \u2014 RENAME ANYTIME" : `${tier === "sports_premium" ? "PREMIUM" : "PRO"} PERK \u2014 RENAME ANYTIME`}
                                    </p>
                                )}
                            </div>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="pp-bg-0 rounded-lg p-4 border border-[#242436]">
                                    <p className="text-xs uppercase font-mono text-[#8b8ba0]">Role</p>
                                    <p className="mt-1 text-white capitalize">{user.role}</p>
                                </div>
                                <div className="pp-bg-0 rounded-lg p-4 border border-[#242436]">
                                    <p className="text-xs uppercase font-mono text-[#8b8ba0] inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Tier expires</p>
                                    <p className="mt-1 text-white" data-testid="account-tier-expires">{expires}</p>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Button asChild className="bg-[#6f3fec] hover:brightness-110 text-white" data-testid="account-upgrade-button">
                                    <Link to="/pricing">{tier === "sports_premium" ? "Extend Subscription" : tier === "pro" ? "Upgrade to Premium" : "Subscribe"}</Link>
                                </Button>
                                <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white" data-testid="account-view-profile-button">
                                    <Link to={`/u/${user.id}`} className="inline-flex items-center gap-1"><ExternalLink className="w-4 h-4" /> View Public Profile</Link>
                                </Button>
                                <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white">
                                    <Link to="/messages">Messages</Link>
                                </Button>
                                {(user.role === "admin" || user.role === "support") && (
                                    <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white">
                                        <Link to="/owner">{user.role === "admin" ? "Owner Dashboard" : "Staff Dashboard"}</Link>
                                    </Button>
                                )}
                                <Button onClick={() => { logout(); nav("/"); }} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white" data-testid="account-logout-button">
                                    Logout
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
        </div>
    );
}
